import React from 'react';
import { useSelector } from 'react-redux';
import Loader from 'components/atoms/loader';
import EmptyState from 'components/atoms/empty_state';
import { buildRobotProposalParams } from 'services/robot_proposal_params';
import { ApplicationState } from 'store';
import { BiddingData } from 'store/modules/bidding_store/types';
import { PanelState } from 'store/modules/panel_store/types';

const ROBOT_URL = '/facilitadores/robo';
const ROBOT_SALE_URL = '/robo_lance';

interface AiManagementBiddingRobotProps {
  bidding: BiddingData | null;
  loading: boolean;
  error: boolean;
}

const AiManagementBiddingRobot: React.FC<AiManagementBiddingRobotProps> = ({
  bidding,
  loading,
  error,
}) => {
  const { panel } = useSelector<ApplicationState, PanelState>((state) => state.session.panel);

  if (loading && !bidding) return <Loader />;
  if (error || !bidding)
    return <EmptyState icon="error_outline" title="Não foi possível carregar a licitação." />;

  const hasRobotAccess = !!(panel?.allow_robot || panel?.validate_services?.robo);
  const trading = bidding.has_electronic_trading ? bidding.electronic_trading : undefined;
  const allowedPortals = (panel?.beebeeate_portals ?? []).map((portal) => portal.source_id);

  let unavailableReason = '';
  if (!trading || !allowedPortals.includes(trading.source_id))
    unavailableReason = 'O portal desta licitação ainda não é atendido pelo Robô de Lances.';
  else if (trading.trading_id === null)
    unavailableReason =
      'O Robô de Lance Inteligente só está disponível para licitações da Lei 10.520.';

  const openInNewTab = (url: string) => window.open(url, '_blank', 'noopener');

  const lockIcon = !hasRobotAccess && (
    <i className="material-icons align-middle mr-1" style={{ fontSize: 18 }}>
      lock
    </i>
  );

  return (
    <div className="card">
      <div className="card-body text-center">
        <div className="d-flex justify-content-center flex-wrap">
          <button
            type="button"
            className="btn btn-primary mx-1 mb-2"
            disabled={!hasRobotAccess || !!unavailableReason}
            onClick={() =>
              openInNewTab(
                `${ROBOT_URL}?${buildRobotProposalParams(
                  bidding,
                )}&routerType=showFilterProposalPage`,
              )
            }
          >
            {lockIcon}
            Cadastrar proposta
          </button>
          <button
            type="button"
            className="btn btn-outline-primary mx-1 mb-2"
            disabled={!hasRobotAccess}
            onClick={() => openInNewTab(ROBOT_URL)}
          >
            {lockIcon}
            Já cadastrei uma proposta
          </button>
        </div>
        {hasRobotAccess ? (
          <small className="d-block text-muted">
            {unavailableReason ||
              'Ao clicar no botão uma nova aba irá abrir para você utilizar o Robô de Lances.'}
          </small>
        ) : (
          <>
            <small className="d-block text-muted mb-2">
              O Robô de Lances não faz parte da sua assinatura.
            </small>
            <button
              type="button"
              className="btn btn-sm btn-success"
              onClick={() => openInNewTab(ROBOT_SALE_URL)}
            >
              Conhecer o Robô de Lances
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AiManagementBiddingRobot;
